import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Linkedin, Mail, Store, FileDown } from 'lucide-react';
import Logo from '../components/Logo';
import SiteDisclaimer from '../components/legal/SiteDisclaimer';

// Public newsroom / press-release page — founder-approved FINAL copy (Sept 3, 2026).
const RELEASE_DATE = 'September 3, 2026';
const DOCX_URL = '/downloads/DealShaq_Press_Release_2026-09-03.docx';

const BODY_INTRO = [
  'DealShaq Technologies, Inc. today publicly introduced its AI-powered surplus food marketplace following a two-year development and operational-readiness buildout. The platform is designed to help financially struggling families access deep-discount groceries while helping grocers efficiently and responsibly monetize non-donated unsold surplus food. The announcement comes on September 3, the third day of Hunger Action Month, building on Feeding America\u2019s national call to action to address rising food insecurity across the country.',
  'For more than six years, DealShaq Founder and CEO Alfred Toussaint has served as a food-rescue volunteer with Cornerstones of Reston, LINK Against Hunger of Herndon and Sterling, and McLean Bible Church\u2019s COVID-era food distribution program. During that time, he has completed two to three rescues a week from leading grocery retailers in Northern Virginia and continues to witness the empathy and professionalism of grocery retail teams, the dedication of charity personnel, and the resilience of families facing food insecurity.',
];

const FOUNDER_QUOTE = {
  text: 'It is from that perch, and from the inputs, encouragement, and lived experiences of the people I have served and served with, that the idea for DealShaq came to fruition. DealShaq exists to stand with them, and with the families we are honored to serve.',
  name: 'Alfred Toussaint',
  title: 'Founder and CEO, DealShaq Technologies, Inc.',
};

const BODY_MID = [
  'A major study by the Pacific Coast Food Waste Commitment found that only 15 to 20 percent of unsold surplus grocery food is donated. The primary culprit is logistics. From a food-rescue volunteer\u2019s perspective, every time you load up your car with donated unsold surplus grocery food for delivery to a food pantry, the equivalent of four other carloads are sent to be recycled or landfilled. DealShaq\u2019s mission is to partner with grocers to help reduce that food loss by diverting, monetizing, and selling, at a discount, the contents of one or more of those four carloads to local neighbors who need that food but struggle to afford it.',
  'Research from the Greater Boston Food Bank shows that food-insecure households need about one hundred dollars more per week to afford enough food without relying on charitable food assistance. With discounts as high as 50 to 75 percent off in-store prices, the DealShaq marketplace delivers two powerful economic boosts to families:',
];

const IMPACT = [
  {
    title: 'Affordability Gap Compression',
    text: 'A weekly shortfall of one hundred dollars shrinks to fifty dollars, or even twenty-five dollars, making the gap far more manageable for a financially struggling head of household.',
  },
  {
    title: 'Purchasing Power Multiplication',
    text: 'Every dollar a financially struggling family spends on DealShaq becomes two to four dollars of food value.',
  },
];

const BODY_STATS = [
  'Closer to home, the inaugural Virginia Hunger Study reports that 1 in 2 families with children across the Commonwealth experienced food insecurity in the past year, with Black and Hispanic Virginians approaching twice the food-insecurity level of their White counterparts, at 63 percent, 53 percent, and 33 percent, respectively. The Capital Area Food Bank\u2019s 2026 Hunger Report found that 38 percent of households (over two million residents) across the District of Columbia, Southern Maryland, and Northern Virginia region (DMV) struggled to access enough food between May 2025 and May 2026. The report projects that over the next two years, some 50,000 households regionwide are at risk of losing some or all of their SNAP benefits due to recent federal legislation. That is an additional 50,000 of our neighbors who will have to make extremely painful decisions about what they can feed their children and which meals to skip.',
  'The data from the above studies reinforce the imperative and urgency for a marketplace like DealShaq in the DMV.',
];

// Ordered content blocks after the economic-impact cards.
// kind: 'section' (perspective heading, serif h2) | 'app' (product heading, emerald h3)
const SECTIONS = [
  {
    kind: 'section',
    heading: 'The Grocer Perspective',
    paras: [
      'Grocery retailers play a central role in community food access, and their teams work every day to manage surplus responsibly, uphold brand standards, and support local families with empathy and professionalism. Their food donations already generate enormous impact by preventing waste, reducing greenhouse-gas emissions, and supporting millions of meals for families in need. DealShaq is built to amplify that impact by providing a responsible, brand-safe channel and real-time sales execution of non-donated unsold surplus food.',
      'DealShaq\u2019s surplus-based, discount-optimized model empowers grocers with flexible, brand-safe discount options tailored to their operational needs and urgency to sell, while ensuring families receive deep, predictable savings. At the same time, unsold surplus food remains one of the most persistent structural challenges in grocery retail. Across the United States, grocers absorb 27 to 30 billion dollars in annual losses from inventory that is stocked, handled, and paid for but never sold, a margin-eroding problem in a sector operating on razor-thin margins. This surplus also carries a heavy environmental cost, contributing to the national food-waste system responsible for significant greenhouse-gas emissions. DealShaq helps grocers recover value from this surplus while reducing its climate footprint.',
      'For an item sold on DealShaq at a 50 percent discount off its in-store price, the equivalent of 52.5 to 64.5 percent of that value is returned through a combination of direct payment from DealShaq, disposal-cost avoidance, and the ordinary-loss tax benefit associated with discounted surplus sales. When grocers divert even a portion of their recycle-bound or landfill-bound surplus to the DealShaq marketplace, the combined impact multiplies: disposal costs avoided, emissions reduced, water waste prevented, millions saved by families, and meaningful revenue returned to grocers. The DealShaq marketplace turns long-standing and long-accepted food loss and waste into margin-positive and community-positive outcomes.',
      'A conservative what-if analysis by DealShaq revealed some hopeful signs. What if just 10 percent of all non-donated unsold surplus grocery food nationwide were diverted and sold at a 50 percent discount on a marketplace like DealShaq? Over 1.35 billion pounds and 1.13 billion meals would have been diverted. Over 4 million tons of greenhouse-gas emissions would have been averted. Over 127 billion gallons of water would not have been wasted. Over 7 million food-insecure people would have been well on their way toward food security. Over 2 billion dollars would have been saved in aggregate by shoppers. And DealShaq would have returned some 1.6 billion dollars directly to grocers, over and above their disposal-cost recovery and ordinary-loss tax benefit.',
      'What this suggests to an individual store is that even the smallest diversion of landfill- or recycle-bound surplus can make a measurable impact in the community, and when combined with existing food donations, that impact is magnified. DealShaq challenges every grocer to match the amount of food they donate with an equal amount sold at a 50-plus percent discount, transforming their unsold surplus food into a dual-channel engine for food-security impact.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Grocer App',
    paras: [
      'The DealShaq Grocer App helps grocers turn non-donated unsold surplus food into steady revenue with positive community impact. Grocers list soon-to-expire and other surplus items in seconds by snapping a shelf photo, scanning a barcode (including PLUs), or batch-posting an aisle, then setting discounts and letting the platform secure and transmit orders in real-time, and schedule pickup and/or DoorDash delivery with live tracking. As a Virginia-registered marketplace facilitator, DealShaq manages sales tax automatically, and a built-in tax center summarizes sales and tax activity for each period.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Grocer Impact Dashboard',
    paras: [
      'The DealShaq Grocer Impact Dashboard provides straightforward sales insights, including revenue, items sold, average order value, top-moving products, inventory-turnover trends, and sales velocity. Grocers can track the pounds of surplus sold on the platform, the meals those pounds represent, the emissions and water waste avoided, the number of food-insecure people positively impacted, the value recovered, weekly digests, and monthly ESG reports. Together, these tools give grocers the operational analytics to understand how their DealShaq-sold surplus is performing, how much impact it is having on the community, all data they can use for their sustainability reports and filings.',
    ],
  },
  {
    kind: 'section',
    heading: 'The Grocery Shopper Perspective',
    paras: [
      'From the shopper perspective, whether a household is financially struggling or simply looking for smart ways to stretch its grocery budget, DealShaq delivers meaningful value through its tiered pricing model. In keeping with the company\u2019s tagline, \u201cWhere Hot Deals Sizzle,\u201d the marketplace offers three discount tiers. The lowest tier, known as a Sizzling Deal, is for items priced at a 50 percent discount off the grocer\u2019s in-store price. The mid-tier discount, known as a Hot Deal, is for items priced at 60 percent off the grocer\u2019s in-store price. The highest tier, known as a Sizzling Hot Deal, is for items priced at a 75 percent discount off the grocer\u2019s in-store price. And while products on the platform are surplus by design, DealShaq does not compromise on quality. The marketplace does not accept expired items, and grocer partners are encouraged to offer good-quality products that reflect well on their brand and on the DealShaq experience. This ensures that families receive deep savings without sacrificing confidence in their food order.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Shopper App',
    paras: [
      'The DealShaq Shopper App is designed to be simple, accessible, and supportive for every shopper, including the 30 to 40 percent of food-pantry clients whose first language is not English. The app ships in English and Spanish, with additional languages planned. It includes a conversational AI shopping assistant that helps users find the right surplus items based on their meal goals, budgets, and dietary needs. Shoppers can type everyday requests like \u201cItalian-themed dinner for four, one has a peanut allergy, for under thirty dollars.\u201d A built-in meal and recipe engine can scale cooking goals into ingredient lists and sourcing plans, and a personalized recommendation feature learns each shopper\u2019s preferences over time. Alternatively, a shopper can upload a grocery list, and the app will turn it into organized recommendations matched to participating grocers\u2019 sizzling hot deals. All of these capabilities are powered by live AI integrations that make the DealShaq shopper experience intuitive, multilingual, and highly responsive.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Shopper Impact Dashboard',
    paras: [
      'With the DealShaq Shopper Impact Dashboard, shoppers can track the total money they have saved, and the environmental benefit of those purchases\u2014greenhouse-gas emissions averted and gallons of water saved. The dashboard highlights the shopper\u2019s Charity of Choice contributions, combining their optional order round-ups with DealShaq\u2019s automatic contribution. Viewable by month, by year, or across all time, these insights help families see not only how far their dollars are stretching, but also how their everyday shopping is reducing food waste.',
    ],
  },
  {
    kind: 'section',
    heading: 'The Food Charity Perspective',
    paras: [
      'Food charities, including food pantries, mobile distribution programs, community-based nonprofits, and faith-based hunger-relief organizations, remain indispensable pillars in addressing food insecurity. Their teams work tirelessly to source donated food, coordinate volunteers, manage logistics, and serve families with dignity, empathy, and respect. Yet even the most dedicated food-charity leaders routinely hear the same difficult question from clients: \u201cCan you help me afford the additional food my family needs?\u201d',
      'DealShaq is designed to stand alongside these charitable organizations by providing families in general, and their clients in particular, with a deep-discount marketplace that complements food assistance, stretches limited budgets, and helps close the affordability gap that food charities alone cannot bridge. DealShaq\u2019s Charity of Choice program enables DealShaq shoppers and grocer location partners to select which charitable organization receives a percentage of their DealShaq purchases and revenue. As the third side of DealShaq\u2019s three-sided marketplace, food charities are uniquely positioned to encourage their clients and grocer donors to select them as their DealShaq charity of choice.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Charity App',
    paras: [
      'The DealShaq Charity App enables charitable organization partners to capture, grow, and manage the contributions they receive through DealShaq\u2019s Charity of Choice program\u2014all from one secure, easy-to-use place. To help charities enhance their total contribution potential, the app includes a ready-to-use recruitment toolkit with a referral code, QR, and branded flyer and landing page that charities can share with clients. Supporters stay anonymous through privacy-safe aggregate metrics, and nonprofits get one-click tax receipts, annual statements, and food-rescue impact tied to their own donors. Access is handled through secure one-time magic links, giving small teams simple, safe sign-in without the headaches of passwords.',
    ],
  },
  {
    kind: 'app',
    heading: 'The DealShaq Charity Impact Dashboard',
    paras: [
      'The DealShaq Charity Impact Dashboard gives nonprofits a clear, reliable view of every contribution in one place, rolling up round-ups, direct gifts, and grocer-partner donations into Total Charitable Contributions. This includes simple monthly or quarterly breakdowns and a payout ledger showing what has been deposited via Stripe and what is pending.',
    ],
  },
  {
    kind: 'section',
    heading: 'Geographic Scope',
    paras: [
      'While nothing in DealShaq\u2019s design precludes it from operating anywhere on the planet, DealShaq is hyper-focused on Northern Virginia through early 2027. The company has been in discussions with local, regional, national, and international grocers with store locations in the DMV, as well as food charities serving the local area, to join DealShaq\u2019s Surplus-Based, Discount-Optimized Pilot Cohort. For the vast majority of grocers and food charities in the region that Team DealShaq has yet to reach, feel free to drop a line via partnerships@dealshaq.com.',
    ],
  },
];

export default function PressRelease() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Top bar */}
      <header className="border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/grocers')}
            className="flex items-center gap-2 group"
            data-testid="press-home-link"
          >
            <Logo size="small" />
          </button>
          <div className="flex items-center gap-4">
            <a
              href={DOCX_URL}
              download
              className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1.5"
              data-testid="press-download-docx"
            >
              <FileDown className="w-4 h-4" /> Download .docx
            </a>
            <a
              href="https://www.linkedin.com/company/106030342"
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-emerald-700 hover:text-emerald-900 flex items-center gap-1.5"
              data-testid="press-linkedin-link"
            >
              <Linkedin className="w-4 h-4" /> Follow on LinkedIn
            </a>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-14">
        {/* Kicker */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-600">Newsroom</span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">For Immediate Release</span>
        </div>

        {/* Headline */}
        <h1
          className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="press-headline"
        >
          DealShaq Technologies, Inc. Advances Hunger Action Month with the Launch of Its Surplus-Based, Discount-Optimized Grocery Access Platform for Financially Struggling Families
        </h1>
        <p className="mt-5 text-xl text-gray-600 leading-relaxed">
          An AI-powered surplus food marketplace launches in Northern Virginia during Hunger Action
          Month, giving financially struggling families deep-discount grocery access while helping
          grocers responsibly monetize non-donated unsold surplus food.
        </p>

        {/* Body — intro */}
        <div className="mt-10 space-y-6 text-lg text-gray-800 leading-relaxed">
          <p>
            <span className="font-semibold text-gray-900">RESTON, Va. &mdash; {RELEASE_DATE} &mdash;</span>{' '}
            {BODY_INTRO[0]}
          </p>
          {BODY_INTRO.slice(1).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* Founder quote */}
        <blockquote className="mt-10 border-l-4 border-amber-500 pl-6 py-1" data-testid="press-quote-0">
          <p className="text-xl text-gray-900 italic leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
            &ldquo;{FOUNDER_QUOTE.text}&rdquo;
          </p>
          <footer className="mt-3 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{FOUNDER_QUOTE.name}</span> &middot; {FOUNDER_QUOTE.title}
          </footer>
        </blockquote>

        {/* Body — mid */}
        <div className="mt-8 space-y-6 text-lg text-gray-800 leading-relaxed">
          {BODY_MID.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* Economic impact */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="press-impact">
          {IMPACT.map((item, i) => (
            <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/60 p-5" data-testid={`press-impact-${i}`}>
              <h3 className="text-base font-bold text-amber-800">{item.title}</h3>
              <p className="mt-2 text-gray-800 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* Body — regional stats */}
        <div className="mt-8 space-y-6 text-lg text-gray-800 leading-relaxed">
          {BODY_STATS.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* Perspective sections + product features */}
        {SECTIONS.map((block, i) => (
          <section key={i} className={block.kind === 'section' ? 'mt-12' : 'mt-8'} data-testid={`press-section-${i}`}>
            {block.kind === 'section' ? (
              <h2
                className="text-2xl sm:text-3xl font-bold text-gray-900"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {block.heading}
              </h2>
            ) : (
              <h3 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
                <span className="h-4 w-1 rounded bg-emerald-500" />
                {block.heading}
              </h3>
            )}
            <div className="mt-4 space-y-6 text-lg text-gray-800 leading-relaxed">
              {block.paras.map((para, j) => (
                <p key={j}>{para}</p>
              ))}
            </div>
          </section>
        ))}

        {/* Boilerplate */}
        <div className="mt-14 border-t border-gray-200 pt-8">
          <h2 className="text-lg font-bold text-gray-900">About DealShaq Technologies, Inc.</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            Headquartered in Reston, Virginia, U.S.A., DealShaq Technologies, Inc. (
            <a href="https://dealshaq.com" className="text-emerald-700 font-medium hover:underline" data-testid="press-about-website-link">dealshaq.com</a>
            ) provides financially struggling families with deep-discount access to groceries by helping
            grocers efficiently and responsibly monetize non-donated unsold surplus food through its
            AI-powered, surplus-based, discount-optimized marketplace built for community impact.
            Hyper-local to Northern Virginia through early 2027, DealShaq partners with local grocers and
            food-charity organizations to strengthen community food access.
          </p>
        </div>

        {/* Media contact */}
        <div className="mt-8 rounded-xl bg-gray-50 p-6" data-testid="press-media-contact">
          <h2 className="text-sm font-semibold tracking-widest uppercase text-gray-500">Media Contact</h2>
          <p className="mt-2 text-gray-800">
            DealShaq Technologies, Inc.<br />
            <a href="mailto:press@dealshaq.com" className="text-emerald-700 hover:underline" data-testid="press-media-email-link">press@dealshaq.com</a>
          </p>
          <p className="mt-4 text-xs text-gray-400">
            # # #
          </p>
          <a
            href={DOCX_URL}
            download
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900"
            data-testid="press-download-docx-footer"
          >
            <FileDown className="w-4 h-4" /> Download this release (.docx)
          </a>
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => navigate('/grocers')}
            data-testid="press-cta-grocers"
          >
            <Store className="w-4 h-4 mr-2" /> Explore DealShaq
          </Button>
          <a href="https://www.linkedin.com/company/106030342" target="_blank" rel="noreferrer" data-testid="press-cta-linkedin-link">
            <Button variant="outline" className="w-full" data-testid="press-cta-linkedin">
              <Linkedin className="w-4 h-4 mr-2" /> Follow our launch
            </Button>
          </a>
          <a href="mailto:partnerships@dealshaq.com" data-testid="press-cta-partnerships-link">
            <Button variant="ghost" className="w-full" data-testid="press-cta-partnerships">
              <Mail className="w-4 h-4 mr-2" /> Become a pilot partner
            </Button>
          </a>
        </div>

        <button
          onClick={() => navigate('/grocers')}
          className="mt-12 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
          data-testid="press-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to DealShaq
        </button>

        <div className="mt-10 border-t border-gray-200 pt-6">
          <SiteDisclaimer />
        </div>
      </article>
    </div>
  );
}
